import reactReplacePlaceholder from 'react-replace-placeholder';

import type { LegWithFormattedDate } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';
import type { StringTranslations } from '@skyscanner-internal/landing-pages-shared-types/types-shared/components';

import STYLES from './DealARIADescriptor.module.scss';

type DealARIADescriptorProps = {
  strings?: StringTranslations;
  direct: boolean;
  price: string;
  legs: LegWithFormattedDate[];
  city?: string;
};

const DEFAULT_STRINGS = {
  component_deals_evolved_aria_price_per_person: '{price} per passenger.',
  component_deals_evolved_aria_city: '{destination_city}.',
  component_deals_evolved_aria_city_country:
    '{destination_city}, {destination_country}.',
  component_deals_evolved_aria_departing_and_returning_date:
    'Departing {departing_date}, returning {returning_date}.',
  component_deals_evolved_aria_departing_date: 'Departing {departing_date}.',
  component_deals_evolved_aria_flexible_ticket: 'Flexible ticket. Read policy.',
  component_deals_evolved_aria_from_price: 'From {price}, select.',
  component_deals_evolved_aria_inbound_direct_leg:
    'Inbound direct flight with {airline_1} departs from {origin_airport} on {departing_date}, arriving in {destination_airport}.',
  component_deals_evolved_aria_inbound_indirect_leg_1:
    'Inbound indirect flight with {airline_1}, departs from {origin_airport} on {departing_date}, arriving in {destination_airport}.',
  component_deals_evolved_aria_inbound_indirect_leg_2:
    'Inbound indirect flight with {airline_1} and {airline_2}, departs from {origin_airport} on {departing_date}, arriving in {destination_airport}.',
  component_deals_evolved_aria_inbound_indirect_leg_3:
    'Inbound indirect flight with {airline_1}, {airline_2} and {airline_3}, departs from {origin_airport} on {departing_date}, arriving in {destination_airport}.',
  component_deals_evolved_aria_one_way_flight_1:
    'One-way flight with {airline_1}.',
  component_deals_evolved_aria_one_way_flight_2:
    'One-way flight with {airline_1} and {airline_2}.',
  component_deals_evolved_aria_one_way_flight_3:
    'One-way flight with {airline_1}, {airline_2} and {airline_3}.',
  component_deals_evolved_aria_outbound_direct_leg:
    'Outbound direct flight with {airline_1} departs from {origin_airport} on {departing_date}, arriving in {destination_airport}.',
  component_deals_evolved_aria_outbound_indirect_leg_1:
    'Outbound indirect flight with {airline_1}, departs from {origin_airport} on {departing_date}, arriving in {destination_airport}.',
  component_deals_evolved_aria_outbound_indirect_leg_2:
    'Outbound indirect flight with {airline_1} and {airline_2}, departs from {origin_airport} on {departing_date}, arriving in {destination_airport}.',
  component_deals_evolved_aria_outbound_indirect_leg_3:
    'Outbound indirect flight with {airlines_1}, {airline_2} and {airline_3}, departs from {origin_airport} on {departing_date}, arriving in {destination_airport}.',
  component_deals_evolved_aria_return_flight_1:
    'Return flight with {airline_1}.',
  component_deals_evolved_aria_return_flight_2:
    'Return flight with {airline_1} and {airline_2}.',
  component_deals_evolved_aria_return_flight_3:
    'Return flight with {airline_1}, {airline_2} and {airline_3}.',
  component_deals_evolved_aria_return_flight_4:
    'Return flight with {airline_1}, {airline_2}, {airline_3} and {airline_4}.',
  component_deals_evolved_aria_return_flight_5:
    'Return flight with {airline_1}, {airline_2}, {airline_3}, {airline_4} and {airline_5}.',
  component_deals_evolved_aria_return_flight_6:
    'Return flight with {airline_1}, {airline_2}, {airline_3}, {airline_4}, {airline_5} and {airline_6}.',
  component_deals_evolved_aria_savings:
    'Save {saving} and fly from {price} per passenger.',
  component_deals_evolved_aria_stop_1: '1 stop in {airport_1}.',
  component_deals_evolved_aria_stop_2:
    '2 stops, in {airport_1} and {airport_2}.',
  component_deals_evolved_aria_taxes: 'Price includes taxes and charges.',
};

const replacePlaceholdersInStrings = (
  strings: StringTranslations,
  stringKeys: string[],
  replacements: StringTranslations,
) =>
  stringKeys.reduce((acc: any, stringKey: string) => {
    const originalString = strings[stringKey];
    let replacedString = originalString;
    Object.entries(replacements).forEach(
      ([replcementKey, replacementValue]) => {
        const placeholder = `{${replcementKey}}`;
        while (replacedString.includes(placeholder)) {
          replacedString = replacedString.replace(
            placeholder,
            replacementValue,
          );
        }
      },
    );
    return { ...acc, [stringKey]: replacedString };
  }, {});

const DealARIADescriptor = ({
  city = undefined,
  direct,
  legs,
  price,
  strings = DEFAULT_STRINGS,
}: DealARIADescriptorProps) => {
  const airlines = [
    ...Array.from(new Set(legs.map((leg) => leg.carrier && leg.carrier.name))),
  ];
  let flightWithAirlineString;
  let travelDatesString;
  const outboundString =
    direct === true
      ? `component_deals_evolved_aria_outbound_direct_leg`
      : `component_deals_evolved_aria_outbound_indirect_leg_1`;
  let inboundString;
  let replacements = {};
  Object.assign(
    replacements,
    ...airlines.map((airline, index) => ({
      [`airline_${index + 1}`]: airline,
    })),
    {
      departing_date: legs[0].formattedDate,
      price,
    },
  );

  if (legs.length === 1) {
    flightWithAirlineString = `component_deals_evolved_aria_one_way_flight_${airlines.length}`;
    travelDatesString = 'component_deals_evolved_aria_departing_date';
  } else {
    flightWithAirlineString = `component_deals_evolved_aria_return_flight_${airlines.length}`;
    travelDatesString =
      'component_deals_evolved_aria_departing_and_returning_date';
    replacements = {
      ...replacements,
      returning_date: legs[1].formattedDate,
    };
    inboundString =
      direct === true
        ? 'component_deals_evolved_aria_inbound_direct_leg'
        : `component_deals_evolved_aria_inbound_indirect_leg_1`;
  }

  const replacedStrings = replacePlaceholdersInStrings(
    strings,
    [
      travelDatesString,
      flightWithAirlineString,
      'component_deals_evolved_aria_from_price',
      'component_deals_evolved_aria_price_per_person',
    ],
    replacements,
  );

  const replacedOutboundString = reactReplacePlaceholder(
    strings[outboundString],
    {
      airline_1: legs[0].carrier.name,
      origin_airport: legs[0].origin.name || legs[0].origin.skyCode,
      destination_airport:
        legs[0].destination.name || legs[0].destination.skyCode,
      departing_date: legs[0].formattedDate,
    },
    '{',
    '}',
  ).join('');

  const replacedInboundString =
    inboundString &&
    reactReplacePlaceholder(
      strings[inboundString],
      {
        airline_1: legs[1].carrier.name,
        origin_airport: legs[1].origin.name || legs[1].origin.skyCode,
        destination_airport:
          legs[1].destination.name || legs[1].destination.skyCode,
        departing_date: legs[1].formattedDate,
      },
      '{',
      '}',
    ).join('');

  const replacedCityString =
    city &&
    reactReplacePlaceholder(
      strings.component_deals_evolved_aria_city,
      {
        destination_city: city,
      },
      '{',
      '}',
    ).join('');

  return (
    <div className={STYLES.DealARIADescriptor}>
      {replacedCityString && <span>{replacedCityString}</span>}
      <span>
        {replacedStrings.component_deals_evolved_aria_price_per_person}
      </span>
      <span>{replacedStrings[travelDatesString]}</span>
      <span>{replacedStrings[flightWithAirlineString]}</span>

      <span>{replacedOutboundString}</span>
      {replacedInboundString && <span>{replacedInboundString}</span>}
      <span>{strings.component_deals_evolved_aria_taxes}</span>
      <span>{replacedStrings.component_deals_evolved_aria_from_price}</span>
    </div>
  );
};

export default DealARIADescriptor;
